// The cat of a floor drawn with three.js (behaviour in cat.js). Nine meshes in one group — body,
// head, four legs, a two-part tail and a contact shadow — on the shared geometries and materials
// (geometries.js, materials.js): a cat owns no GPU resource, so removing it leaks nothing. It is
// never a pick target: the scene's picking ignores it and its meshes do not raycast.
import { Group, Mesh } from 'three';
import { CAT_MODEL } from './geometries.js';
import { catArea, createCat, stepCat, catWakeAt, wantsFrames } from './cat.js';

function noRaycast() {}

export class CatView {
  /**
   * @param {{geos:object, mats:object, layout:object, parent:import('three').Object3D, seed?:number, now?:number,
   *   at?:{x:number, z:number, yaw?:number}|null}} opts
   *   layout: the floor layout (layout.js floorLayout); now: wall clock (s); at: where it was before a rebuild
   */
  constructor({ geos, mats, layout, parent, seed = 1, now = 0, at = null }) {
    const M = CAT_MODEL;
    this.parent = parent;
    this.state = createCat({ area: catArea(layout), seed, now, at });
    this.group = new Group();
    this.group.name = 'cat';
    this.group.scale.setScalar(M.scale);
    this.meshes = [];
    const part = (geo, mat, parentObj, x, y, z, cast = true) => {
      const m = new Mesh(geo, mat);
      m.position.set(x, y, z);
      m.castShadow = cast;
      m.receiveShadow = cast;
      m.raycast = noRaycast;
      parentObj.add(m);
      this.meshes.push(m);
      return m;
    };
    // Soft contact shadow under the body, on the ground whatever the posture.
    this.blob = part(geos.plane, mats.contact, this.group, 0, 0.004, 0, false);
    this.torso = part(geos.catBody, mats.cat, this.group, 0, M.hipY, M.hipZ);
    this.legFL = part(geos.catLegFront, mats.cat, this.torso, M.frontX, 0, M.frontZ);
    this.legFR = part(geos.catLegFront, mats.cat, this.torso, -M.frontX, 0, M.frontZ);
    this.legBL = part(geos.catLegHind, mats.cat, this.torso, M.hindX, 0, 0);
    this.legBR = part(geos.catLegHind, mats.cat, this.torso, -M.hindX, 0, 0);
    this.head = part(geos.catHead, mats.cat, this.torso, 0, M.neckY, M.neckZ);
    this.tail1 = part(geos.catTail1, mats.cat, this.torso, 0, M.tailY, M.tailZ);
    this.tail2 = part(geos.catTail2, mats.cat, this.tail1, 0, 0, -M.tail1);
    // The tail turns (sway, wrap) around the body's up axis, then lifts (curls) in its own heading.
    this.tail1.rotation.order = 'YXZ';
    this.tail2.rotation.order = 'YXZ';
    parent.add(this.group);
    this.apply();
  }

  /**
   * Advances the behaviour and poses the meshes.
   * @param {number} dt seconds
   * @param {number} now wall clock (s)
   * @param {object} env cat.js stepCat env
   * @returns {boolean} true when the cat needs the next frame
   */
  update(dt, now, env) {
    const before = this.state.mode === 'rest' && this.state.posed;
    const want = stepCat(this.state, dt, now, env);
    // A still cat has nothing new to pose (no work while it rests).
    if (!(before && !want)) this.apply();
    return want;
  }

  /** Wall clock (s) of the next wake-up of a resting cat (cat.js catWakeAt). */
  wakeAt(env) {
    return catWakeAt(this.state, env);
  }

  /** True while the cat moves. */
  get moving() {
    return wantsFrames(this.state);
  }

  /** Writes the pose of the state into the meshes (no allocation). */
  apply() {
    const s = this.state;
    const M = CAT_MODEL;
    this.group.position.set(s.x, 0, s.z);
    this.group.rotation.y = s.yaw;
    // The shadow follows the body: longer standing, rounder sitting.
    const sit = clampUnit(-s.pitch / 0.7);
    this.blob.position.z = M.hipZ + 0.13 - 0.06 * sit;
    this.blob.scale.set(0.42 + 0.06 * sit, 1, 0.72 - 0.26 * sit);
    this.torso.position.set(0, M.hipY + s.hipY + s.bob, M.hipZ + s.hipZ);
    this.torso.rotation.x = s.pitch;
    this.legFL.rotation.x = s.fl;
    this.legFR.rotation.x = s.fr;
    this.legBL.rotation.x = s.bl;
    this.legBR.rotation.x = s.br;
    this.legFL.scale.y = s.reach;
    this.legFR.scale.y = s.reach;
    this.head.rotation.set(s.headPitch, s.headYaw, 0);
    this.tail1.rotation.set(s.tailLift, s.tailSway, 0);
    this.tail2.rotation.set(s.tailCurl, s.tailWrap, s.tailRoll);
  }

  /** Follows the floor's sun shadow setting (only the current floor casts). */
  setCastShadow(on) {
    for (const m of this.meshes) if (m !== this.blob) m.castShadow = on;
  }

  /** Removes the cat (its geometries and materials are shared: not disposed here). */
  dispose() {
    this.parent.remove(this.group);
    this.group.clear();
    this.meshes = [];
  }
}

function clampUnit(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
